import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  href?: string;
}
// Simple utility for class merging if not already present
function classNames(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(' ');
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  href,
  children,
  ...props
}, ref) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';
  const variants = {
    primary: 'bg-[#d4af37] text-white hover:bg-[#c49d2f] hover:shadow-lg hover:-translate-y-0.5 font-semibold',
    secondary: 'bg-[#2B0A3D] text-white hover:bg-[#1a0625] hover:shadow-lg hover:-translate-y-0.5',
    outline: 'bg-[#2b0a3d] text-white hover:opacity-90 active:opacity-85 focus:ring-[#2b0a3d]/30 hover:shadow-lg hover:-translate-y-0.5',
    ghost: 'text-[#2B0A3D] hover:bg-[#2B0A3D]/10'
  };
  const sizes = {
    sm: 'h-9 px-4 text-sm',
    md: 'h-11 px-6 text-base',
    lg: 'h-14 px-8 text-lg',
    xl: 'h-16 px-10 text-xl'
  };
  const classes = classNames(baseStyles, variants[variant], sizes[size], fullWidth ? 'w-full' : '', className);
  if (href) {
    return <motion.a href={href} className={classes} whileTap={{
      scale: 0.98
    }} data-id="element-218">
          {children}
          <ArrowRight className="w-5 h-5" data-id="element-219" />
        </motion.a>;
  }
  return <motion.button ref={ref} className={classes} whileTap={{
    scale: 0.98
  }} {...props} data-id="element-220">
        {children}
        <ArrowRight className="w-5 h-5" data-id="element-221" />
      </motion.button>;
});
Button.displayName = 'Button';